<template>
  <div class="pricing-overlay" @click.self="dismiss">
    <div class="pricing-box">
      <!-- Close button -->
      <button class="pricing-close" @click="dismiss">
        ×
      </button>

      <!-- Header -->
      <div class="pricing-modal-header">
        <div class="header-icon" v-if="mode === 'upsell'">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        </div>
        <div class="header-icon header-icon--featured" v-else-if="mode === 'featured'">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
        </div>
        <div class="header-icon header-icon--limit" v-else>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        </div>
        <!-- Copy only promises what the plans actually do (modules/planConfig.js,
             modules/adExpiry.js, modules/featuredPlacement.js). Featured copy depends on
             whether placement is switched on (FEATURED_PLACEMENT). -->
        <h2 class="modal-title" v-if="mode === 'upsell'">Post Published!</h2>
        <h2 class="modal-title" v-else-if="mode === 'featured' && featuredEnabled">Get Your Ad Featured</h2>
        <h2 class="modal-title" v-else-if="mode === 'featured'">Featured Placement Is Coming Soon</h2>
        <h2 class="modal-title" v-else-if="limitKind === 'images'">Photo Limit Reached</h2>
        <h2 class="modal-title" v-else>Post Limit Reached</h2>
        <!-- Say when a Free ad expires up front, so the expiry email isn't a surprise -->
        <template v-if="mode === 'upsell' && expiryDateLabel">
          <p class="modal-expiry">
            Your ad is live until <strong>{{ expiryDateLabel }}</strong> ({{ expiryDays }} days).
            We'll email you before it expires — renewing is free and takes one click from <strong>My Ads</strong>.
          </p>
          <p class="modal-subtitle">Upgrade to post more, add more photos, and keep your ads up longer — paid plans renew them automatically.</p>
        </template>
        <p class="modal-subtitle" v-else-if="mode === 'upsell'">Your ad is live! Upgrade to post more, add more photos, and keep your ads up longer — paid plans renew them automatically.</p>
        <p class="modal-subtitle" v-else-if="mode === 'featured' && featuredEnabled">Featured ads show at the top of matching search results for {{ featuredDays }} days, with a Featured badge. Premium includes 1 a month, Business 5.</p>
        <p class="modal-subtitle" v-else-if="mode === 'featured'">We're still building featured placement. Upgrade now for more posts, more photos and ads that stay up longer.</p>
        <p class="modal-subtitle" v-else-if="limitKind === 'images'">You've reached the photo limit for one post on the {{ planLabel }} plan. Upgrade to add more photos.</p>
        <p class="modal-subtitle" v-else>You've used all {{ postsLimit }} posts this month on the {{ planLabel }} plan. Upgrade to keep posting.</p>
      </div>

      <!-- Plan cards. Radio-selectable with ONE primary action at the bottom:
           a per-card Upgrade button made the two plans compete, and gave the
           decline path ("Continue") the same visual weight as buying.
           Prices are normalised to a monthly figure because Business bills
           quarterly — shown raw ($9.99 vs $49) the cheaper plan reads as the
           dearer one. Feature copy mirrors modules/planConfig.js. -->
      <div class="modal-plans" role="radiogroup" aria-label="Choose a plan">
        <label class="modal-plan" v-if="showPremium" :class="{ 'is-selected': selectedPlan === 'premium' }">
          <input type="radio" name="pricing-plan" class="plan-radio-input" value="premium" v-model="selectedPlan" />
          <span class="plan-radio" aria-hidden="true"></span>
          <span class="plan-left">
            <span class="plan-badge plan-badge--blue">Popular</span>
            <span class="plan-name">Premium</span>
            <span class="plan-desc">25 posts/mo · 15 photos · ads renew automatically<template v-if="featuredEnabled"> · 1 featured/mo</template></span>
          </span>
          <span class="plan-right">
            <span class="plan-price">$9.99<small>/mo</small></span>
            <span class="plan-billing">billed monthly</span>
          </span>
        </label>

        <label class="modal-plan" v-if="showBusiness" :class="{ 'is-selected': selectedPlan === 'business' }">
          <input type="radio" name="pricing-plan" class="plan-radio-input" value="business" v-model="selectedPlan" />
          <span class="plan-radio" aria-hidden="true"></span>
          <span class="plan-left">
            <span class="plan-badge plan-badge--dark">Best value</span>
            <span class="plan-name">Business</span>
            <span class="plan-desc">Unlimited posts · 30 photos · ads never expire<template v-if="featuredEnabled"> · 5 featured/mo</template></span>
          </span>
          <span class="plan-right">
            <span class="plan-price">$16.33<small>/mo</small></span>
            <span class="plan-billing">billed $49 every 3 months</span>
          </span>
        </label>
      </div>

      <!-- One primary action, driven by the selected radio. The decline path
           is a plain text button so it reads as secondary, not as a rival CTA. -->
      <!-- Only when there is something to buy. A user already on the top plan
           can still reach this modal in "featured" mode (FEATURED_LIMIT_REACHED),
           and both cards are hidden then — leaving a dead, disabled button as
           the most prominent thing on screen. -->
      <button v-if="hasPlans" class="modal-upgrade" :disabled="upgrading || !selectedPlan" @click="upgrade()">
        {{ upgrading ? 'Starting checkout…' : upgradeLabel }}
      </button>

      <button class="modal-skip" @click="dismiss">
        {{ mode === 'upsell' ? 'Continue to my post' : 'Not now' }}
      </button>

    </div>
  </div>
</template>

<script>
import { createCheckout, useSubscription, planRank } from "@/composables/useSubscription";
import { trackUpgradeShown, trackUpgradeClick, trackUpgradeDismissed } from "@/utils/upgradeFunnel";
import { toast } from "vue-sonner";
import { getFeaturedConfig } from "@/utils/featuredPlacement";

export default {
  name: "PricingModal",
  props: {
    mode: { type: String, default: "upsell" }, // "upsell" | "limit" | "featured"
    postsLimit: { type: Number, default: 5 },
    // For mode "limit": which limit was hit — "posts" or "images"
    limitKind: { type: String, default: "posts" },
    // For mode "upsell": when the just-posted ad expires (null = never)
    adExpiresAt: { type: [String, Date], default: null },
  },
  emits: ["close"],
  data() {
    return { upgrading: false, selectedPlan: null, featuredEnabled: false, featuredDays: 7 };
  },
  mounted() {
    trackUpgradeShown(this.source);
    getFeaturedConfig().then(({ enabled, days }) => {
      this.featuredEnabled = enabled;
      this.featuredDays = days;
    });
  },
  created() {
    // Preselect the cheapest upgrade available, so the default is the smallest
    // commitment rather than the largest. Buying still needs an explicit click.
    this.selectedPlan = this.showPremium
      ? "premium"
      : this.showBusiness
      ? "business"
      : null;
  },
  computed: {
    expiryDate() {
      const d = this.adExpiresAt ? new Date(this.adExpiresAt) : null;
      return d && !isNaN(d) && d > Date.now() ? d : null;
    },
    expiryDateLabel() {
      return this.expiryDate
        ? this.expiryDate.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
        : "";
    },
    expiryDays() {
      return this.expiryDate ? Math.round((this.expiryDate - Date.now()) / 86400000) : 0;
    },
    currentPlan() {
      return useSubscription().currentPlan.value;
    },
    // Funnel source: which moment showed this modal
    source() {
      return `modal-${this.mode === "limit" ? `limit-${this.limitKind}` : this.mode}`;
    },
    planLabel() {
      return { premium: "Premium", business: "Business" }[this.currentPlan] || "Free";
    },
    showPremium() {
      return planRank(this.currentPlan) < planRank("premium");
    },
    showBusiness() {
      return planRank(this.currentPlan) < planRank("business");
    },
    hasPlans() {
      return this.showPremium || this.showBusiness;
    },
    upgradeLabel() {
      if (!this.selectedPlan) return "Select a plan";
      return this.selectedPlan === "business"
        ? "Upgrade to Business — $49"
        : "Upgrade to Premium — $9.99";
    },
  },
  methods: {
    dismiss() {
      trackUpgradeDismissed(this.source);
      this.$emit("close");
    },
    async upgrade(plan = this.selectedPlan) {
      if (!plan) return;
      const token = localStorage.getItem("auth-token");
      if (!token) {
        toast.error("Please sign in to upgrade.");
        return;
      }
      this.upgrading = true;
      trackUpgradeClick(this.source, plan);
      try {
        await createCheckout(plan, this.source);
      } catch (err) {
        toast.error(err.response?.data?.error || "Failed to start checkout.");
        this.upgrading = false;
      }
    },
  },
};
</script>

<style scoped>
.pricing-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.45);
  /* -webkit- first: unprefixed backdrop-filter only landed in Safari 18, so
     on iOS 17 and below — including the WKWebView the Capacitor app runs in —
     the blur silently does nothing without it. Matches HeroSection/
     AdvanceFilter, which already prefix it. */
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Scroll when the box is taller than the viewport (upsell mode on small
     phones) — otherwise the Continue/Skip buttons are clipped with no way to
     reach them. safe-area padding keeps the edges clear of notch/indicator. */
  overflow-y: auto;
  padding: calc(16px + env(safe-area-inset-top, 0px))
    calc(16px + env(safe-area-inset-right, 0px))
    calc(16px + env(safe-area-inset-bottom, 0px))
    calc(16px + env(safe-area-inset-left, 0px));
}

/* Centering + overflow clips the top of an overflowing flex child; anchor to
   the top instead once the screen is small enough for that to happen. */
@media (max-width: 600px) {
  .pricing-overlay {
    align-items: flex-start;
  }
}

.pricing-box {
  position: relative;
  background: #fff;
  border-radius: 20px;
  padding: 36px 28px 24px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  animation: modalSlide 0.25s ease-out;
}

@keyframes modalSlide {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

.pricing-close {
  position: absolute;
  top: 16px;
  right: 16px;
  /* 44px is Apple's minimum tap target; at 40 this was the one control in the
     modal below it, and it is the one people reach for to dismiss. */
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: #f0f0f0;
  color: #333;
  font-size: 28px;
  font-weight: 300;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.pricing-close:hover {
  background: #e0e0e0;
  color: #000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.pricing-modal-header {
  text-align: center;
  margin-bottom: 24px;
}

.header-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: #f0fdf4;
  color: #16a34a;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}

.header-icon--limit {
  background: #fef3c7;
  color: #d97706;
}

.header-icon--featured {
  background: #fffbeb;
  color: #d97706;
}

.modal-title {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 4px;
}

.modal-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.modal-expiry {
  font-size: 14px;
  line-height: 1.5;
  color: #065f46;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 0 0 10px;
  text-align: left;
}

.modal-plans {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.modal-plan {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1.5px solid #e5e7eb;
  border-radius: 14px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}

.modal-plan:hover {
  border-color: #3467d5;
  box-shadow: 0 2px 12px rgba(52, 103, 213, 0.1);
}

.modal-plan.is-selected {
  border-color: #3467d5;
  background: #f5f8ff;
  box-shadow: 0 2px 12px rgba(52, 103, 213, 0.12);
}

/* The native control stays in the DOM (keyboard + screen readers) but is
   visually replaced by .plan-radio, which styles consistently across
   browsers. Not display:none — that would drop it from the tab order. */
.plan-radio-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.plan-radio {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
  position: relative;
}

.modal-plan.is-selected .plan-radio {
  border-color: #3467d5;
}

.modal-plan.is-selected .plan-radio::after {
  content: "";
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  background: #3467d5;
}

.plan-radio-input:focus-visible + .plan-radio {
  box-shadow: 0 0 0 3px rgba(52, 103, 213, 0.35);
}

.plan-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.plan-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 2px 8px;
  border-radius: 6px;
  margin-bottom: 4px;
}

.plan-badge--blue { background: #eff6ff; color: #3467d5; }
.plan-badge--dark { background: #f1f5f9; color: #334155; }

.plan-name {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.plan-desc {
  display: block;
  font-size: 12px;
  color: #6b7280;
  margin: 2px 0 0;
}

.plan-right {
  text-align: right;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

/* The real billing terms, kept next to the normalised monthly figure so the
   headline number is comparable without hiding what actually gets charged. */
.plan-billing {
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
}

.plan-price {
  display: block;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 2px;
}

.plan-price small {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

.modal-upgrade {
  display: block;
  width: 100%;
  padding: 13px 16px;
  border: none;
  border-radius: 10px;
  background: #3467d5;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  font-family: var(--font-primary, "Inter", sans-serif);
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
}

.modal-upgrade:hover:not(:disabled) {
  background: #2857c0;
}

.modal-upgrade:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-skip {
  display: block;
  width: 100%;
  padding: 10px;
  border: none;
  background: none;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
  transition: color 0.15s;
}

.modal-skip:hover {
  color: #374151;
}

@media (max-width: 480px) {
  .pricing-box {
    padding: 28px 18px 18px;
  }
  /* Keep the row direction so the radio stays beside the plan it selects —
     stacking it put the control on its own line, detached from the label.
     The price block moves under the name instead. */
  .modal-plan {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .plan-radio {
    margin-top: 2px;
  }
  .plan-right {
    width: 100%;
    text-align: left;
    align-items: flex-start;
    padding-left: 32px;
    margin-top: 6px;
  }
  .plan-price {
    font-size: 17px;
  }
}
</style>
